import { useCallback, useMemo } from 'react';

import { Button, FlexAligner, Typography } from '@scaleaq/scaleui';
import { InventoryTree, SiteControlEvent } from '@scaleaq/sitecontrol-lib';
import { ColumnFiltersState, PaginationState, SortingState, Updater } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { EventTableConfig } from '@/components/eventComponent/';
import { Table, type ColumnType } from '@/components/table/';
import { useGetAllLastMessages } from '@/dataAccess/realTime/internal/useGetAllLastMessages.ts';
import { useLayoutProps } from '@/hooks';
import { capitalizeAndSplitOnUpperCase, findSensorInInventory, responsive } from '@/utils';

import { useInventoryContext } from '../InventoryProvider/InventoryContext.ts';
import { AccessorKeys } from './getColumns.tsx';
import { getEventField } from './liveEvents/getEventField.ts';
import { sortEvents } from './liveEvents/sortEvents.ts';

type LiveEventsProps = {
  columns: ColumnType<SiteControlEvent>[];
  configInput?: EventTableConfig;
  defaultTableConfig: EventTableConfig;
  id: string;
  inventory: InventoryTree[];
};

export const LiveEvents = ({
  columns,
  configInput,
  defaultTableConfig,
  id,
  inventory,
}: LiveEventsProps) => {
  const { t } = useTranslation(['eventStream']);
  const { getInventoryNameById } = useInventoryContext();
  const { config, update: updateEventTableConfig } = useLayoutProps<EventTableConfig>({
    widgetId: id,
    configInput,
    defaultConfig: { ...defaultTableConfig, title: t('eventStream:eventStream') },
  });

  const {
    livePageSize,
    liveDefaultFilters: columnFilters,
    liveDefaultSorting: sorting = [{ id: 'timestamp', desc: true }],
  } = config;

  const {
    isLoading: liveConnectionLoading,
    messages,
    isEnabled,
    setIsEnabled,
  } = useGetAllLastMessages({
    enable: true,
    listenerId: 'Live-events',
    maxMessages: 1000,
  });

  const fullTableData = useMemo<SiteControlEvent[]>(() => {
    if (messages) {
      return messages.map(message => ({
        id: message.context.id,
        type: capitalizeAndSplitOnUpperCase(message.eventName),
        sender: message.context.sender,
        inventoryId: message.context.sourceId,
        timestamp: String(message.context.timestamp),
        data: message.data,
      }));
    }
    return [];
  }, [messages]);

  const pagination: PaginationState = useMemo<PaginationState>(
    () => ({
      pageIndex: 0,
      pageSize: livePageSize || 1000,
    }),
    [livePageSize]
  );

  const handleSortingChange = useCallback(
    (sorting: Updater<SortingState>) => {
      const resolvedSorting =
        typeof sorting === 'function' ? sorting(config.liveDefaultSorting) : sorting;
      updateEventTableConfig({
        widgetId: id,
        config: {
          ...config,
          liveDefaultSorting: resolvedSorting,
        },
      });
    },
    [config, id, updateEventTableConfig]
  );

  const handleColumnFiltersChange = useCallback(
    (columnFilters: Updater<ColumnFiltersState>) => {
      const resolvedFilters =
        typeof columnFilters === 'function'
          ? columnFilters(config.liveDefaultFilters)
          : columnFilters;

      updateEventTableConfig({
        widgetId: id,
        config: {
          ...config,
          liveDefaultFilters: resolvedFilters,
        },
      });
    },
    [config, id, updateEventTableConfig]
  );

  const filteredEvents = useMemo(() => {
    if (columnFilters.length > 0) {
      let filtered = [...fullTableData];

      for (const filter of columnFilters) {
        const { id } = filter;
        const value: string = filter.value as string;

        if (id === AccessorKeys.timestamp) {
          const filterFromDate = value?.[0]
            ? new Date(value[0] as string).getTime()
            : Date.now() - 24 * 60 * 60 * 1000;
          const filterToDate = value?.[1] ? new Date(value[1] as string).getTime() : Date.now();

          filtered = filtered.filter(event => {
            const eventTimestamp = new Date(event.timestamp).getTime();
            return eventTimestamp >= filterFromDate && eventTimestamp <= filterToDate;
          });
        } else if (id === AccessorKeys.sourcePath) {
          const lowerValue = value.toString().toLowerCase();
          filtered = filtered.filter(event => {
            const sensor = findSensorInInventory(inventory, event.inventoryId);
            return sensor?.name.toLowerCase().includes(lowerValue);
          });
        } else {
          const lowerValue = value.toString().toLowerCase();
          filtered = filtered.filter(event => {
            const eventValue = getEventField(event, id);
            return eventValue.includes(lowerValue);
          });
        }
      }
      return filtered;
    }
    return fullTableData;
  }, [columnFilters, fullTableData, inventory]);

  const sortedEvents = useMemo(() => {
    if (sorting.length > 0) {
      const [{ id, desc }] = sorting;
      return filteredEvents?.sort((e1, e2) => sortEvents(e1, e2, id, desc, getInventoryNameById));
    }
    return filteredEvents;
  }, [filteredEvents, getInventoryNameById, sorting]);

  return (
    <>
      {liveConnectionLoading && <Typography>{t('liveConnectionLoading')}</Typography>}
      <div>
        <FlexAligner
          style={{
            paddingBottom: responsive.spacing['xlarge'],
            paddingRight: responsive.spacing['7xlarge'],
          }}
          alignment='center-right'
        >
          <Button onClick={() => setIsEnabled(!isEnabled)}>
            {isEnabled ? t('eventStream:pauseEvents') : t('eventStream:startEvents')}
          </Button>
        </FlexAligner>
        <Table
          columns={columns}
          data={sortedEvents}
          key={uuidv4()}
          onSortingChange={handleSortingChange}
          onColumnFiltersChange={handleColumnFiltersChange}
          rowCount={sortedEvents.length}
          state={{ pagination, sorting, columnFilters }}
          withPagination={false}
        />
      </div>
    </>
  );
};
